 
import React from "react";
import { PageLoader } from "../../../../../../components/PageLoader/PageLoader";
import { StandadCard } from "../../../../../../components/Cards";
import { useSelector } from "react-redux";
import { IStoreState } from "../../../../../../redux/initialStoreState";
import { LoadState, MODULE_IDS } from "../../../../../../constants/enums";
import { useCommAutovehcilesList, useDispatchWrapper, useBreadcrumb, useCustomerPolicyDetails } from "../../../../../../hooks";
import { useFormik } from "formik";
import { Box, Grid, SelectChangeEvent } from "@mui/material";
import {
  ControlledCustomSelect,
  CustomFormLabel,
  CustomTextField,
} from "../../../../../../components/formsComponents";
import { useNavigate, useParams } from "react-router-dom";
import { CustomDatePicker } from "../../../../../../components/formsComponents/CustomDatePicker";
import { renderDropdownValue } from "../../../../../../helpers";
import { RoleBasedCustomButton } from "../../../../../../security/RoleBasedComponents/RoleBasedCustomButton/RoleBasedCustomButton";
import { fetchCommAutomobileVehicleListAsync } from "../../../../../../redux/commAutoPolicy/commAutoVehicle/commAutoVehicleActions";
import {
  addCommAutomobileOcep28Async,
  clearCommAutoPolicyFormsState,
  fetchCommAutomobileOcep28AAsync,
} from "../../../../../../redux/commAutoPolicy/commAutoPolicyForms/commAutoPolicyFormsActions";
import produce from "immer";
import { PageContainer } from "../../../../../../components/container/PageContainer";
import { COMMON_STATUS } from "../../../../../../constants/constants";

export const CommAutoOPCF_28A: React.FC<{ isDuplicate?: boolean }> = (
  props
) => {
  const { customerId, customerPolicyId, serialNumber } = useParams() as {
    customerId?: number;
    customerPolicyId?: number;
    serialNumber?: number;
  };

  useBreadcrumb({
    title: "",
    items: [
    {
      to: "/dashboard",
      title: "dashboard",
    },
    {
      to: "/view-customers",
      title: "customers",
    },
    {
      to: "/customer-overview/" + customerId,
      title: "customer-overview",
    },
    {
      to:
        "/comm-automobile-policy/comm-automobile/" +
        customerId +
        "/" +
        customerPolicyId,
      title: "comm-auto",
    },
    {
      title: "opcf-28A",
    },
  ],
  });

  const { isDuplicate } = props;
  const isUpdate = serialNumber ? true : false;


  const {
    data: opcf,
    loading: opcfLoading,
    error,
  } = useSelector(
    (storeState: IStoreState) => storeState.commAuto.policyForms.opcf28A
  );
  const { details: policyAdmin } = useCustomerPolicyDetails(customerPolicyId);

  const { vehicleDropdownlist, vehiclesList } = useCommAutovehcilesList(
    true,
    customerPolicyId
  );

  const [saveLoading, setSaveLoading] = React.useState(false);
  const dispatch = useDispatchWrapper();
  const navigate = useNavigate();

  const { values, handleChange, handleSubmit, setFieldValue, setValues } =
    useFormik({
      initialValues: opcf,
      validate: (values) => {},
      onSubmit: (values) => {
        if (customerPolicyId && customerId) {
          setSaveLoading(true);
          dispatch(
            addCommAutomobileOcep28Async(
              {
                ...values,
                customer_policy_id: customerPolicyId,
                customer_id: customerId || 0,
                producer_one_id: policyAdmin?.data?.producer_one,
                producer_one_code: policyAdmin?.data?.producer_one_code,
                type: "opcf",
              },
              opcf,
              isUpdate,
              (isSuccess) => {
                if (isSuccess) {
                  window.scrollTo(0, 0);
                  navigate(
                    `/comm-automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}`
                  );
                }
                setSaveLoading(false);
              },
              isDuplicate
            )
          );
        }
      },
    });

  const handleAutomobileNumberChange = (e: SelectChangeEvent<unknown>) => {
    const value = e.target.value as string;
    const newValues = produce(values, (draftValues) => {
      const vehicle = vehiclesList.find((item) => item.vin === value);
      if (vehicle) {
        draftValues.automobile_number = value;
        draftValues.year = vehicle.year;
        draftValues.trade_name = vehicle.make;
        draftValues.model = vehicle.model;
        draftValues.serial_number_or_vin = vehicle.vin;
      }
    });
    setValues(newValues);
  };

  React.useEffect(() => {
    if (customerPolicyId && serialNumber) {
      dispatch(fetchCommAutomobileOcep28AAsync(customerPolicyId, serialNumber, "opcf"));
    }
  }, [customerPolicyId, serialNumber]);

  React.useEffect(() => {
    if (customerPolicyId) {
      dispatch(fetchCommAutomobileVehicleListAsync(customerPolicyId));
    }
  }, [customerPolicyId, serialNumber]);

  React.useEffect(() => {
    if (!serialNumber) {
      setValues({
        ...opcf,
        issued_to: policyAdmin?.data?.customer_name,
        policy_number: policyAdmin?.data?.policy_number,
        producer_one_id: policyAdmin?.data?.producer_one,
        producer_one_code: policyAdmin?.data?.producer_one_code,
      });
    } else {
      setValues(opcf);
    }
  }, [opcf, serialNumber, policyAdmin]);

  React.useEffect(() => {
    return () => {
      dispatch(clearCommAutoPolicyFormsState());
    };
  }, []);

  return (
    <PageContainer
      title={
        serialNumber ? "Edit CommAuto Opcf 28A" : "Create CommAuto Opcf 28A"
      }
      description="this is inner page ."
    >
      <PageLoader
        loading={opcfLoading === LoadState.InProgress}
        error={error ? { message: error } : undefined}
      >
        <StandadCard sx={{ mt: 2 }} heading="OPCF 28A Excluded Driver">
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={1}>
                <CustomFormLabel>#</CustomFormLabel>
                <CustomTextField
                  name="incremental_number"
                  variant="outlined"
                  size="small"
                  type="text"
                  fullWidth
                  value={values.incremental_number}
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <CustomFormLabel>Issued to</CustomFormLabel>
                <CustomTextField
                  name="issued_to"
                  variant="outlined"
                  size="small"
                  type="text"
                  fullWidth
                  disabled
                  value={values.issued_to}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <CustomFormLabel>Effective Date of Change</CustomFormLabel>
                <CustomDatePicker
                  fullWidth
                  value={values.effective_date_of_change || ""}
                  onChange={(newValue) => {
                    setFieldValue("effective_date_of_change", newValue);
                  }}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <CustomFormLabel>Policy Number</CustomFormLabel>
                <CustomTextField
                  name="policy_number"
                  variant="outlined"
                  size="small"
                  type="text"
                  fullWidth
                  disabled
                  value={values.policy_number}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <CustomFormLabel>Automobile #</CustomFormLabel>
                <ControlledCustomSelect
                  variant="outlined"
                  size="small"
                  type="string"
                  name="automobile_number"
                  fullWidth
                  value={values.automobile_number}
                  onChange={handleAutomobileNumberChange}
                  displayEmpty
                  renderValue={renderDropdownValue(`Select one`)}
                  placeholder="Select one"
                  options={vehicleDropdownlist}
                ></ControlledCustomSelect>
              </Grid>

              <Grid item xs={12} md={2}>
                <CustomFormLabel>Model</CustomFormLabel>
                <CustomTextField
                  name="model"
                  variant="outlined"
                  size="small"
                  type="text"
                  fullWidth
                  disabled
                  value={values.model}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <CustomFormLabel>Year</CustomFormLabel>
                <CustomDatePicker
                  fullWidth
                  value={values.year || ""}
                  inputFormat="YYYY"
                  views={["year"]}
                  disabled
                  onChange={(newValue) => {
                    setFieldValue("year", newValue);
                  }}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <CustomFormLabel>Trade Name (Make)</CustomFormLabel>
                <CustomTextField
                  name="trade_name"
                  variant="outlined"
                  size="small"
                  type="text"
                  disabled
                  fullWidth
                  value={values.trade_name}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <CustomFormLabel>Serial # /VIN</CustomFormLabel>
                <CustomTextField
                  name="serial_number_or_vin"
                  variant="outlined"
                  size="small"
                  type="text"
                  disabled
                  fullWidth
                  value={values.serial_number_or_vin}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <CustomFormLabel>Name of Excluded Driver</CustomFormLabel>
                <CustomTextField
                  name="name_of_excluded_driver"
                  variant="outlined"
                  size="small"
                  type="text"
                  fullWidth
                  value={values.name_of_excluded_driver}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomFormLabel>Driver's Licence #</CustomFormLabel>
                <CustomTextField
                  name="driver_license_number"
                  variant="outlined"
                  size="small"
                  type="text"
                  fullWidth
                  value={values.driver_license_number}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} lg={3}>
              <CustomFormLabel>Reminder Date</CustomFormLabel>
              <CustomDatePicker
                fullWidth
                value={values.reminder_date || ""}
                onChange={(newValue) => {
                  setFieldValue("reminder_date", newValue);
                }}
              />
            </Grid>
            <Grid item xs={12} lg={3}>
                <CustomFormLabel>Status</CustomFormLabel>
                <ControlledCustomSelect
                  fullWidth
                  value={values.status}
                  name="status"
                  onChange={handleChange}
                  placeholder="Select one"
                  displayEmpty
                  options={COMMON_STATUS}
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 4 }}>
              <RoleBasedCustomButton
                sx={{ width: "15%" }}
                moduleKey={MODULE_IDS.COMMERCIAL_AUTO}
                disabled={saveLoading}
                variant="contained"
                type="submit"
              >
                Save
              </RoleBasedCustomButton>
            </Box>
          </form>
        </StandadCard>
        {/* </CustomerBaiscDetailsLayout> */}
      </PageLoader>
    </PageContainer>
  );
};
